import React, { useEffect, useRef, useState } from 'react'
import { Modal, Alert, Spin, Typography } from 'antd'
import { Html5Qrcode } from 'html5-qrcode'

const { Text } = Typography

const SCANNER_ID = 'qr-scanner-region'

const QrScannerModal = ({ open, onScan, onClose, loading = false }) => {
  const scannerRef = useRef(null)
  const timerRef = useRef(null)
  const [cameraError, setCameraError] = useState(null)

  const stopScanner = async () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
    const qr = scannerRef.current
    // Clear ref first to allow re-entrancy / fast reopen
    scannerRef.current = null
    if (qr) {
      try {
        // `stop()` returns a promise; not awaiting can leave the camera locked.
        await qr.stop()
      } catch {}
      try {
        // Remove video/canvas elements from the region.
        await qr.clear()
      } catch {}
    }
  }

  const startScanner = () => {
    if (scannerRef.current) return
    setCameraError(null)

    const el = document.getElementById(SCANNER_ID)
    if (!el) {
      setCameraError('Scanner element not found. Please try closing and reopening.')
      return
    }

    const qr = new Html5Qrcode(SCANNER_ID)
    scannerRef.current = qr

    qr.start(
      { facingMode: 'environment' },
      { fps: 10, qrbox: { width: 250, height: 250 } },
      (decodedText) => {
        stopScanner()
        onScan(decodedText)
      },
      () => {}
    ).catch((err) => {
      scannerRef.current = null
      const msg = err?.message || ''
      if (msg.toLowerCase().includes('permission') || msg.toLowerCase().includes('denied')) {
        setCameraError('Camera permission denied. Please allow camera access and try again.')
      } else {
        setCameraError('Camera not available. Make sure you are on HTTPS and your device has a camera.')
      }
    })
  }

  useEffect(() => {
    if (!open) {
      stopScanner()
      setCameraError(null)
      return
    }
    // Delay to let the Modal animation finish and the DOM element mount
    timerRef.current = setTimeout(startScanner, 400)

    return () => {
      stopScanner()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const handleClose = () => {
    stopScanner()
    setCameraError(null)
    onClose()
  }

  return (
    <Modal
      title="Scan Product QR Code"
      open={open}
      onCancel={handleClose}
      footer={null}
      destroyOnClose
    >
      {cameraError && (
        <Alert
          type="error"
          message={cameraError}
          style={{ marginBottom: 16 }}
        />
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: 40 }}>
          <Spin size="large" />
          <div style={{ marginTop: 12 }}>
            <Text type="secondary">Loading product…</Text>
          </div>
        </div>
      ) : (
        <>
          <div id={SCANNER_ID} style={{ width: '100%' }} />
          <div style={{ textAlign: 'center', marginTop: 12 }}>
            <Text type="secondary">
              Point your camera at a product QR code to add it automatically.
            </Text>
          </div>
        </>
      )}
    </Modal>
  )
}

export default QrScannerModal
